import {
  ActionIcon,
  Group,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VisuallyHidden,
} from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import {
  Archive,
  ArrowUp,
  Box,
  CircleAlert,
  Frame,
  LaptopMinimal,
  Speech,
  Type,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { Voice } from "../../../../api/generated/model";
import { ErrorAlert } from "../../../../platform/ErrorAlert";
import { AddActorsDialog } from "../../../../platform/actors/AddActorsDialog";
import { ActorsComposer } from "../../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor } from "../../../../platform/actors/picked";
import { useMakings, type Making } from "../../../../platform/actors/use-makings";
import {
  ProductListPopover,
  type ListedProduct,
} from "../../../../platform/media/ProductListPopover";
import { ScriptActions, ScriptBusy } from "../../../../platform/scripts/ScriptActions";
import { ScriptOverlay } from "../../../../platform/scripts/ScriptOverlay";
import { useScriptJobs } from "../../../../platform/scripts/use-script-jobs";
import { useVoicePages } from "../../../../platform/voices/VoiceList";
import { Choice } from "../../../../ui/Choice";
import { GlassPanel } from "../../../../ui/GlassPanel";
import { ModelPicker } from "../../../../ui/ModelPicker";
import { Price } from "../../../../ui/Price";
import { Soon } from "../../../../ui/Soon";
import { SourceTool } from "../../../../ui/SourceTool";
import { UsageNotes } from "../../../../ui/UsageNotes";
import { RatioGlyph } from "../../../../ui/RatioGlyph";
import type { ToolFormProps } from "../form-parts";
import { StatusButton, type StatusStep } from "../StatusButton";
import { VIBE_PLACEHOLDER, advancedModels, useAdvancedStarts } from "./advanced";
import {
  MAX_ACTORS,
  audioBlockers,
  audioButton,
  castOf,
  recast,
  type Cast,
} from "./cast";
import { CastRow } from "./CastRow";
import { CastVoice } from "./CastVoice";
import type { SpeechAudio } from "./speech";
import { SpeechSection } from "./SpeechSection";
import classes from "./talking-head.module.css";
import { useRenders } from "./use-renders";
import { useTakes } from "./use-takes";
import { useVoiceovers } from "./use-voiceovers";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";

/** Live's words (`placeholders.ts`, `generate-audios-button/constants.ts`, `uft-estimate`). */
const PLACEHOLDER =
  "Write your script... Use [emotion] to add emotions like [laughs], [sights], [excited]";
const FAILED = "Failed to generate audio";
const STALE = "The script was changed and you should re-generate the audio";
/** Live's brief placeholder in Talking Head's "Generate Script" (`script-generation-overlay`). */
const BRIEF = "Describe what your avatars should say...";
/** Live's shortest script the smiley enhances in Talking Head (`MIN_SCRIPT_LENGTH`). */
const MIN_SCRIPT = 10;
const UNPRICED = "We couldn’t calculate the estimated generation cost right now.";
/** Live's "Auto" for the Advanced ratio and resolution (`TALKING_HEAD_AUTO_VALUE`). */
const AUTO = [{ value: "auto", label: "Auto" }];
/** Live holds "Audio generated" this long before "Re-generate Audios". */
const MADE_HOLD_MS = 3000;

type AudioMode = "tts" | "sts";

/** Live's audio sources (`audio-mode-select`, capture 07b), each with its icon. */
const AUDIO_MODES: { value: AudioMode; label: string; icon: ReactNode }[] = [
  { value: "tts", label: "Text to speech", icon: <Type size={16} strokeWidth={1.5} /> },
  { value: "sts", label: "Speech to speech", icon: <Speech size={16} strokeWidth={1.5} /> },
];

const STEPS: Record<ReturnType<typeof audioButton>, StatusStep> = {
  "Generate Audios": "generate",
  "Generating Audio": "generating",
  "Audio generated": "generated",
  "Re-generate Audios": "regenerate",
};

interface TalkingHeadFormProps {
  /** The Pro Studio redesign's "Actor reads my script" and "Actor in a scene" in place of the
   *  Advanced switch: the same Advanced off and on (spec ui-perf/pro-studio-redesign). */
  styles?: boolean | undefined;
}

/**
 * Live's Talking Head (issue 238, `talking-head-form`, captures 07, 07b, L09 to L09g), its
 * simple flow: one to four actors; with text to speech each with its voice, the script and
 * "Generate Audios", one voiceover per actor; with speech to speech one "Speech" for every actor;
 * then the start, one video per actor on Vivid or Swift. Live's script tools (issue 224) write
 * or enhance the script. With Advanced on (issue 223, capture 07c), where the catalog offers it,
 * each actor speaks the script in its voice, directed by a vibe, about the product if one is
 * chosen: no voiceovers, one run per actor on Kling v3 Pro. "Add AI Actor" shows the actor
 * composer in the form's place (live's `actor-generation-shell`): one portrait, kept as the
 * team's actor once made, or one upload.
 */
export function TalkingHeadForm(props: ToolFormProps & TalkingHeadFormProps) {
  const { catalog, above, overlay, onStarted, actor, inline, styles } = props;
  const { teamId, brandId } = useParams({ from: ROUTE });
  // Live's two talking-head models, Vivid first: the catalog's rows of the job type.
  const models = catalog.video_models.filter((each) => each.job_types.includes("video.talking_head"));
  const [modelId, setModelId] = useState(() => (models.find((each) => each.is_default) ?? models[0])?.id);
  const model = models.find((each) => each.id === modelId) ?? models[0];
  // Live's Advanced: its one row, Kling v3 Pro, locked; the switch waits for the catalog's.
  const advancedModel = advancedModels(catalog.video_models)[0];
  const [advanced, setAdvanced] = useState(false);
  const [vibe, setVibe] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  // An actor AI Actors' "Create a Talking Head" brought comes in with its own voice.
  const [casts, setCasts] = useState<Cast[]>(() =>
    actor === null || actor === undefined ? [] : [castOf(actor)],
  );
  const [script, setScript] = useState("");
  // Live's audio source: each actor's voiceover of the script, or one speech for every actor.
  const [mode, setMode] = useState<AudioMode>("tts");
  const [speech, setSpeech] = useState<SpeechAudio | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  // A voiceover has been asked for since the form opened: "Re-generate Audios" from then on.
  const [generated, setGenerated] = useState(false);
  // Live's warning once the script changes under made voiceovers, until they are made again.
  const [stale, setStale] = useState(false);
  // Live's "Generate Script" overlay, and the product it writes about (the form's, kept).
  const [scriptOpen, setScriptOpen] = useState(false);
  const [product, setProduct] = useState<ListedProduct | null>(null);
  const [productsOpen, setProductsOpen] = useState(false);
  // Live's "Add AI Actor": the actor composer in the form's place, and the actors it is making.
  const [makingActor, setMakingActor] = useState(false);
  const [makings, setMakings] = useState<Making[]>([]);
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      setCasts((current) => [...current, castOf(pickedOfActor(made))]);
    },
    onFailed: (jobId) =>
      setMakings((current) =>
        current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each)),
      ),
  });
  // The actors' own voices, to play them and to offer them as "Recommended Voice".
  const globals = useVoicePages(teamId, brandId, { kind: "global" });
  const voices = globals.data?.pages.flatMap((page) => page.items) ?? [];
  const sampleOf = (voiceId: string) => voices.find((each) => each.id === voiceId)?.preview_url ?? null;
  const takes = useTakes(teamId, casts.map((cast) => cast.take));
  const running = takes.some((take) => take.state === "running");
  const failed = takes.some((take) => take.state === "failed");
  const voiceovers = useVoiceovers({
    teamId,
    brandId,
    model: catalog.audio_models.find((each) => each.job_types.includes("audio.voiceover")),
    casts,
    // Speech to speech and Advanced make no voiceovers.
    script: mode === "tts" && !advanced ? script : "",
    onStarted: (jobs) => {
      setGenerated(true);
      setStale(false);
      setCasts((current) =>
        current.map((cast) => ({ ...cast, take: jobs[cast.actor.key] ?? cast.take })),
      );
    },
  });
  // Each mode prices and starts only while it is on.
  const renders = useRenders({
    teamId,
    brandId,
    model,
    casts: advanced ? [] : casts,
    speaking: mode === "tts" ? { mode, takes, script } : { mode, speech },
    onStarted,
  });
  const runs = useAdvancedStarts({
    teamId,
    brandId,
    model: advancedModel,
    casts: advanced ? casts : [],
    draft: { script, vibe, productId: product?.productId ?? null },
    onStarted,
  });
  const busy = renders.starting || runs.starting;
  // From the click on Generate Audios, not only once the voiceovers run: a script or voice
  // changed while their starts are on their way is not what they read (live's race, its lock
  // waits for the job's status).
  const locked = busy || running || voiceovers.starting;
  // Live's "Audio generated" for 3 s once the last voiceover is made.
  const made = takes.length > 0 && takes.every((take) => take.state === "done");
  // The hold is the moment's own: a new script or voice within it leaves it to run out.
  const [justMade, setJustMade] = useState(false);
  const wasRunning = useRef(false);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (wasRunning.current && !running && made) {
      setJustMade(true);
      clearTimeout(hold.current);
      hold.current = setTimeout(() => setJustMade(false), MADE_HOLD_MS);
    }
    wasRunning.current = running;
  }, [running, made]);
  useEffect(() => () => clearTimeout(hold.current), []);

  // Live's switch clears the voiceovers, so it waits while they are made.
  const advancedLocked = running || voiceovers.starting;
  const turnAdvanced = (on: boolean) => {
    if (on === advanced) return;
    setAdvanced(on);
    setCasts((current) => current.map((cast) => ({ ...cast, take: null })));
  };
  const blockers = audioBlockers(casts, script, makings.length > 0);
  const word = audioButton({ generated, running: running || voiceovers.starting, justMade });
  const editingCast = casts.find((cast) => cast.actor.key === editing) ?? null;
  const update = (actorId: string, change: (cast: Cast) => Cast) =>
    setCasts((current) => current.map((cast) => (cast.actor.key === actorId ? change(cast) : cast)));
  // Live's rule: a new script clears every voiceover made, with its warning.
  const write = (next: string) => {
    setScript(next);
    if (casts.some((cast) => cast.take !== null) && !running) {
      setCasts((current) => current.map((cast) => ({ ...cast, take: null })));
      setStale(true);
    }
  };
  // Live's script tools: a script or an enhancement lands as an edit does (live's `updateScript`).
  const scripts = useScriptJobs({
    teamId,
    brandId,
    tool: "talking_head",
    onScript: (text) => write(text),
  });
  if (model === undefined) return null;
  if (makingActor) {
    // Live hides the form and its Actions while the composer is open; the form keeps its state.
    const close = () => setMakingActor(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        inline={inline}
        onStarted={(job) => {
          setMakings((current) => [...current, { jobId: job.id, failed: false }]);
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            setCasts((current) => [...current, ...made.map((each) => castOf(pickedOfActor(each)))]);
            close();
          },
        }}
      />
    );
  }
  const block = model.modes["video.talking_head"];
  // Live's tools wait while a script, an enhancement or a voiceover is under way.
  const toolsFree = !locked && !scripts.processing;
  const productTool = (
    <ProductListPopover
      teamId={teamId}
      brandId={brandId}
      chosenId={product?.productId ?? null}
      opened={productsOpen}
      onClose={() => setProductsOpen(false)}
      onPick={setProduct}
      position="top-start"
      anchor={(wrap) => (
        <SourceTool
          label="Products"
          icon={<Archive size={20} strokeWidth={1.5} />}
          view={product}
          removeLabel="Remove the product"
          onRemove={() => setProduct(null)}
          onClick={() => setProductsOpen((open) => !open)}
          open={productsOpen}
          disabled={scripts.generating}
          target={wrap}
        />
      )}
    />
  );
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={(next) => {
        setCollapsed(next);
        setEditing(null);
      }}
      header={
        <div className={classes.header}>
          {styles === true ? (
            // The Pro Studio redesign's two styles: Advanced off, then on.
            <SegmentedControl
              aria-label="Talking head style"
              radius="xl"
              size="xs"
              value={advanced ? "scene" : "script"}
              onChange={(value) => turnAdvanced(value === "scene")}
              data={[
                { value: "script", label: "Actor reads my script", disabled: advancedLocked },
                {
                  value: "scene",
                  label: "Actor in a scene",
                  disabled: advancedModel === undefined || advancedLocked,
                },
              ]}
            />
          ) : advancedModel === undefined ? (
            // Live's Advanced (issue 223): Coming soon until the catalog offers its row.
            <Soon>
              {(soon) => (
                <Switch {...soon} label="Advanced" size="sm" checked={false} onChange={() => undefined} />
              )}
            </Soon>
          ) : (
            <Switch
              label="Advanced"
              size="sm"
              checked={advanced}
              disabled={advancedLocked}
              onChange={() => turnAdvanced(!advanced)}
            />
          )}
        </div>
      }
      summary={<span>{advanced ? "Talking Head - Advanced" : "Talking Head"}</span>}
      above={above}
      overlay={overlay}
      cover={
        scriptOpen ? (
          <ScriptOverlay
            placeholder={BRIEF}
            footerLeft={productTool}
            pending={scripts.generating}
            error={scripts.generateError}
            onCancel={() => setScriptOpen(false)}
            onGenerate={(brief) =>
              scripts.generate(brief, product?.productId ?? null, () => setScriptOpen(false))
            }
          />
        ) : undefined
      }
      aside={
        editingCast === null ? undefined : (
          <CastVoice
            key={editingCast.actor.key}
            teamId={teamId}
            brandId={brandId}
            cast={editingCast}
            recommended={voices.find((each) => each.id === editingCast.actor.voice?.id) ?? null}
            designModel={catalog.audio_models.find((each) => each.job_types.includes("voice.design"))}
            onVoice={(voice: Voice) =>
              // Live's rule: a new voice clears that actor's voiceover.
              update(editingCast.actor.key, (cast) => ({ ...cast, picked: voice, take: null }))
            }
            onStability={(stability) => update(editingCast.actor.key, (cast) => ({ ...cast, stability }))}
            onClose={() => setEditing(null)}
          />
        )
      }
      footer={
        advanced ? (
          <Group justify="space-between" wrap="nowrap" gap={12}>
            <Group gap={12} wrap="nowrap">
              {/* The overlay holds the product while it is open. */}
              {scriptOpen ? null : productTool}
              <Choice
                setting="Model"
                icon={<Box size={20} strokeWidth={1.5} />}
                chevron={false}
                value={advancedModel?.id ?? ""}
                options={[{ value: advancedModel?.id ?? "", label: advancedModel?.name ?? "" }]}
                onChange={() => undefined}
                disabledBecause="Model"
              />
              <Choice
                setting="Aspect ratio"
                tip="Aspect ratio"
                icon={<Frame size={20} strokeWidth={1.5} />}
                chevron={false}
                value="auto"
                options={AUTO}
                onChange={() => undefined}
              />
              <Choice
                setting="Resolution"
                tip="Resolution"
                icon={<LaptopMinimal size={20} strokeWidth={1.5} />}
                chevron={false}
                value="auto"
                options={AUTO}
                onChange={() => undefined}
              />
            </Group>
            <Group gap={12} wrap="nowrap">
              {/* Live quotes no Advanced run: each step is charged as it runs. */}
              <Price priced={undefined} pending={false} unpriced={UNPRICED} />
              <ActionIcon
                variant="filled"
                size="lg"
                radius="100%"
                aria-label="Start"
                // Live's "AI Actor generation must finish first", in both modes.
                disabled={!runs.canStart || locked || scripts.processing || makings.length > 0}
                loading={runs.starting}
                onClick={() => runs.start()}
              >
                <ArrowUp size={20} />
              </ActionIcon>
            </Group>
          </Group>
        ) : (
          <Group justify="space-between" wrap="nowrap" gap={12}>
            <Group gap={12} wrap="nowrap">
              <ModelPicker models={models} value={model.id} onChange={setModelId} />
              {locked ? (
                <Choice
                  setting="Setup mode"
                  icon={AUDIO_MODES.find((each) => each.value === mode)?.icon}
                  chevron={false}
                  value={mode}
                  options={AUDIO_MODES}
                  onChange={() => undefined}
                  disabledBecause="Setup mode"
                />
              ) : (
                <Choice
                  setting="Setup mode"
                  tip="Setup mode"
                  icon={AUDIO_MODES.find((each) => each.value === mode)?.icon}
                  chevron={false}
                  value={mode}
                  options={AUDIO_MODES}
                  onChange={(next) => {
                    setEditing(null);
                    setMode(next as AudioMode);
                  }}
                />
              )}
              <Choice
                setting="Aspect ratio"
                icon={<RatioGlyph ratio="9:16" />}
                chevron={false}
                value="9:16"
                options={[{ value: "9:16", label: "9:16" }]}
                onChange={() => undefined}
                disabledBecause="Aspect ratio"
              />
              <Choice
                setting="Resolution"
                icon={<LaptopMinimal size={20} strokeWidth={1.5} />}
                chevron={false}
                value={renders.resolution ?? ""}
                options={(block?.resolutions?.values ?? []).map((each) => ({ value: each, label: each }))}
                onChange={() => undefined}
                disabledBecause="Resolution"
              />
              <UsageNotes notes={block?.notes ?? []} />
            </Group>
            <Group gap={12} wrap="nowrap">
              <Price priced={renders.priced} pending={renders.pricing} unpriced={UNPRICED} />
              <ActionIcon
                variant="filled"
                size="lg"
                radius="100%"
                aria-label="Start"
                // Live's "AI Actor generation must finish first".
                disabled={!renders.canStart || locked || scripts.processing || makings.length > 0}
                loading={renders.starting}
                onClick={() => renders.start()}
              >
                <ArrowUp size={20} />
              </ActionIcon>
            </Group>
          </Group>
        )
      }
    >
      <Stack gap={16}>
        <CastRow
          casts={casts}
          takes={advanced ? [] : takes}
          sampleOf={sampleOf}
          full={casts.length + makings.length >= MAX_ACTORS}
          disabled={locked}
          voiceless={!advanced && mode === "sts"}
          onRemove={(actorId) => {
            setCasts((current) => current.filter((cast) => cast.actor.key !== actorId));
            if (editing === actorId) setEditing(null);
          }}
          onVoice={setEditing}
          onFromLibrary={() => setAdding(true)}
          inline={inline}
          makings={makings}
          onMakeActor={
            actorModels(catalog.image_models).length === 0
              ? null
              : () => {
                  setEditing(null);
                  setMakingActor(true);
                }
          }
          onDropMaking={(jobId) =>
            setMakings((current) => current.filter((each) => each.jobId !== jobId))
          }
        />
        {advanced ? (
          <Stack gap={4}>
            <Text className={classes.scriptLabel}>Vibe / Direction</Text>
            <Textarea
              variant="unstyled"
              aria-label="Vibe / Direction"
              placeholder={VIBE_PLACEHOLDER}
              autosize
              minRows={2}
              maxRows={6}
              disabled={locked}
              value={vibe}
              onChange={(event) => setVibe(event.currentTarget.value)}
            />
          </Stack>
        ) : null}
        {!advanced && mode === "sts" ? (
          <SpeechSection
            teamId={teamId}
            brandId={brandId}
            caps={block?.talking_head ?? null}
            speech={speech}
            onSpeech={setSpeech}
            disabled={locked}
          />
        ) : (
          <>
            <Stack gap={4}>
              <div className={classes.scriptHead}>
                <Text className={classes.scriptLabel}>Script</Text>
                <ScriptActions
                  canGenerate={toolsFree}
                  // Live's Advanced offers no enhancement (`isEnhanceDisabled`).
                  canEnhance={!advanced && toolsFree && script.trim().length >= MIN_SCRIPT}
                  enhancing={scripts.enhancing}
                  failed={scripts.scriptFailed}
                  onGenerate={() => setScriptOpen(true)}
                  onEnhance={() => scripts.enhance(script)}
                />
              </div>
              <ScriptBusy busy={scripts.processing}>
                <Textarea
                  variant="unstyled"
                  aria-label="Script"
                  placeholder={PLACEHOLDER}
                  autosize
                  minRows={3}
                  maxRows={8}
                  disabled={locked || scripts.processing}
                  value={script}
                  onChange={(event) => write(event.currentTarget.value)}
                />
              </ScriptBusy>
              {scripts.enhanceError === null ? null : <ErrorAlert error={scripts.enhanceError} />}
            </Stack>
            {advanced ? null : (
              <div className={classes.audios}>
                <StatusButton
                  step={STEPS[word]}
                  label={word}
                  disabled={
                    blockers.length > 0 || locked || scripts.processing || !voiceovers.canStart
                  }
                  loading={voiceovers.starting}
                  blockers={blockers}
                  onClick={() => {
                    // Live's Generate Audios closes Voice Settings (`submitGenerate`).
                    setEditing(null);
                    voiceovers.start();
                  }}
                />
                <Price priced={voiceovers.priced} pending={voiceovers.pricing} unpriced={UNPRICED} />
                {failed ? <Mark label={FAILED} tone="failed" /> : null}
                {stale && !failed && !running ? <Mark label={STALE} tone="stale" /> : null}
              </div>
            )}
          </>
        )}
        {(advanced ? [] : renders.problems).map((problem) => (
          <Text key={problem} className={classes.problem} role="alert">
            {problem}
          </Text>
        ))}
        {/* Each mode says only its own steps' problems. */}
        {(advanced
          ? [runs.error]
          : [voiceovers.quoteError, voiceovers.error, renders.quoteError, renders.error]
        ).map((error, at) => (error === null ? null : <ErrorAlert key={at} error={error} />))}
      </Stack>
      {adding ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={casts.map((cast) => cast.actor)}
          max={MAX_ACTORS - makings.length}
          onClose={() => setAdding(false)}
          onConfirm={(actors) => setCasts((current) => recast(current, actors))}
        />
      ) : null}
    </GlassPanel>
  );
}

/** Live's marks beside Generate Audios: a failure in red, a stale script in amber. */
function Mark({ label, tone }: { label: string; tone: "failed" | "stale" }) {
  return (
    <Tooltip label={label} withArrow>
      <span className={classes[tone]}>
        <CircleAlert size={20} aria-hidden />
        <VisuallyHidden>{label}</VisuallyHidden>
      </span>
    </Tooltip>
  );
}

